/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/role_permissions-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-role_permissions-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/role_permissions-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, role_id, permission_id
 * - Sortable fields: created_at
 * - Required fields: id
 * - Readonly fields: id, created_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/role_permissions-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-role_permissions-api-client.ts (API client)
 * - client/api/generated_schemas/role_permissions.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-06-22T23:44:13.685Z
 * Source config: config/entities/role_permissions-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RolePermissionsClient,
  RolePermissionsQueryOptions,
} from "../generated_clients/generated-role_permissions-api-client.js";
import {
  RolePermissionsInsert,
  RolePermissionsUpdate,
  RolePermissionsFieldConfig,
} from "../../types/generated_schemas/generated-role_permissions-schema.js";

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/role_permissions_business_hooks/

// Query keys with enhanced caching strategy
export const rolePermissionsKeys = {
  all: ["role_permissions"] as const,
  lists: () => [...rolePermissionsKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...rolePermissionsKeys.lists(), { filters }] as const,
  details: () => [...rolePermissionsKeys.all, "detail"] as const,
  detail: (id: string) => [...rolePermissionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) =>
    [...rolePermissionsKeys.all, "search", query, fields] as const,
  filter: (filters: Record<string, any>) =>
    [...rolePermissionsKeys.all, "filter", filters] as const,
  sort: (sortBy: string, ascending: boolean) =>
    [...rolePermissionsKeys.all, "sort", sortBy, ascending] as const,
  count: (filters?: Record<string, any>) =>
    [...rolePermissionsKeys.all, "count", filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useRolePermissionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new RolePermissionsClient();
};

/**
 * Hook to get all role_permissions with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with role_permissions data
 */
export const useRolePermissionsList = (
  options?: RolePermissionsQueryOptions,
) => {
  const client = useRolePermissionsClient();

  return useQuery({
    queryKey: rolePermissionsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single role_permission by ID
 *
 * @param id - The role_permission ID
 * @returns React Query result with single role_permission data
 */
export const useRolePermissions = (id: string) => {
  const client = useRolePermissionsClient();

  return useQuery({
    queryKey: rolePermissionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/role_permissions
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/role_permissions_business_hooks/
 */

/**
 * Hook to create a new role_permission
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at
 *
 * @returns React Query mutation for creating role_permission
 */
export const useCreateRolePermissions = () => {
  const client = useRolePermissionsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RolePermissionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolePermissionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing role_permission
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at
 *
 * @returns React Query mutation for updating role_permission
 */
export const useUpdateRolePermissions = () => {
  const client = useRolePermissionsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RolePermissionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: rolePermissionsKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: rolePermissionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a role_permission
 *
 * @returns React Query mutation for deleting role_permission
 */
export const useDeleteRolePermissions = () => {
  const client = useRolePermissionsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolePermissionsKeys.all });
    },
  });
};

/**
 * Hook to count role_permissions with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, role_id, permission_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useRolePermissionsCount = (
  filter?: Partial<
    Record<(typeof RolePermissionsFieldConfig.filterable)[number], any>
  >,
) => {
  const client = useRolePermissionsClient();

  return useQuery({
    queryKey: rolePermissionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useRolePermissionsFieldConfig = () => {
  return RolePermissionsFieldConfig;
};
